// AWS event-stream framing for the Transcribe streaming websocket (TT-1102).
//
// Just the two message shapes the host-audio lane needs — an outbound
// AudioEvent and the inbound TranscriptEvent / exception — hand-rolled so the
// browser bundle carries no AWS SDK. Wire format:
//
//   [total length u32][headers length u32][prelude crc u32]
//   [headers…][payload…][message crc u32]
//
// Each header is [name len u8][name][type u8][value]; every header we care
// about is a string (type 7): [value len u16][value]. CRCs are IEEE CRC-32,
// the prelude's over its first 8 bytes, the message's over everything before
// it.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

const HEADER_TYPE_STRING = 7;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function encodeHeaders(headers) {
  const parts = [];
  let size = 0;
  Object.entries(headers).forEach(([name, value]) => {
    const nameBytes = encoder.encode(name);
    const valueBytes = encoder.encode(value);
    const part = new Uint8Array(1 + nameBytes.length + 1 + 2 + valueBytes.length);
    const view = new DataView(part.buffer);
    let offset = 0;
    part[offset] = nameBytes.length;
    offset += 1;
    part.set(nameBytes, offset);
    offset += nameBytes.length;
    part[offset] = HEADER_TYPE_STRING;
    offset += 1;
    view.setUint16(offset, valueBytes.length);
    offset += 2;
    part.set(valueBytes, offset);
    parts.push(part);
    size += part.length;
  });
  const out = new Uint8Array(size);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
}

export function encodeMessage(headers, payload) {
  const headerBytes = encodeHeaders(headers);
  const totalLength = 4 + 4 + 4 + headerBytes.length + payload.length + 4;
  const message = new Uint8Array(totalLength);
  const view = new DataView(message.buffer);
  view.setUint32(0, totalLength);
  view.setUint32(4, headerBytes.length);
  view.setUint32(8, crc32(message.subarray(0, 8)));
  message.set(headerBytes, 12);
  message.set(payload, 12 + headerBytes.length);
  view.setUint32(totalLength - 4, crc32(message.subarray(0, totalLength - 4)));
  return message;
}

// Transcribe's audio frame: raw PCM in the payload, three fixed headers. An
// empty payload tells Transcribe the stream is over.
export function encodeAudioEvent(pcmBytes) {
  return encodeMessage(
    {
      ":content-type": "application/octet-stream",
      ":event-type": "AudioEvent",
      ":message-type": "event",
    },
    pcmBytes
  );
}

function decodeHeaders(bytes) {
  const headers = {};
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 0;
  while (offset < bytes.length) {
    const nameLength = bytes[offset];
    offset += 1;
    const name = decoder.decode(bytes.subarray(offset, offset + nameLength));
    offset += nameLength;
    const type = bytes[offset];
    offset += 1;
    // Only string headers appear on the messages we receive; anything else
    // would mean a protocol we don't speak, so fail loudly rather than skew.
    if (type !== HEADER_TYPE_STRING) {
      throw new Error(`event-stream: unsupported header type ${type} for ${name}`);
    }
    const valueLength = view.getUint16(offset);
    offset += 2;
    headers[name] = decoder.decode(bytes.subarray(offset, offset + valueLength));
    offset += valueLength;
  }
  return headers;
}

// → { headers, payload (Uint8Array) }. Verifies both CRCs.
export function decodeMessage(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const totalLength = view.getUint32(0);
  const headersLength = view.getUint32(4);
  if (view.getUint32(8) !== crc32(bytes.subarray(0, 8))) {
    throw new Error("event-stream: prelude crc mismatch");
  }
  if (view.getUint32(totalLength - 4) !== crc32(bytes.subarray(0, totalLength - 4))) {
    throw new Error("event-stream: message crc mismatch");
  }
  const headers = decodeHeaders(bytes.subarray(12, 12 + headersLength));
  const payload = bytes.subarray(12 + headersLength, totalLength - 4);
  return { headers, payload };
}

// Parse one inbound Transcribe message into { type, event, payload }:
//   type "event"     → event = ":event-type" (TranscriptEvent), payload = JSON
//   type "exception" → event = ":exception-type", payload = { Message }
export function decodeTranscribeMessage(buffer) {
  const { headers, payload } = decodeMessage(buffer);
  const type = headers[":message-type"];
  const text = payload.length ? decoder.decode(payload) : "";
  let body = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch (e) {
      body = { Message: text };
    }
  }
  return {
    type,
    event: headers[":event-type"] || headers[":exception-type"] || null,
    payload: body,
  };
}
